import React, { useEffect, useMemo, useState } from 'react';

import { formatCurrency } from '~libs/dataUtils';
import { getErrorMessage } from '~libs/axiosWrapper';

import { Button, ErrorResponse, Spinner } from '~components';
import Modal from '~components/Modal';
import { Column, TableView } from '~components/table-view';

import { useGetPaymentBatchClientInvoicesQuery } from '~direct-debits/actions/paymentBatches';
import { formatBatchInvoiceDate } from '~direct-debits/lib/formatBatchDate';
import { PaymentBatchClient } from '~direct-debits/models';

interface ComponentProps {
  line: PaymentBatchClient | null;
  batchId: string | null;
  isOpen: boolean;
  isSubmitting?: boolean;
  // Failure from the last confirm attempt, shown inline and persistently.
  errorMessage?: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

const HEADER_ID = 'dd-reverse-modal-header';

const ReverseModal: React.FC<ComponentProps> = ({
  line,
  batchId,
  isOpen,
  isSubmitting = false,
  errorMessage = null,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  // Mirror the latest submit error locally so it persists until the operator
  // edits the form — which both surfaces it prominently and blocks an immediate
  // identical re-attempt (the Confirm button stays disabled while it's shown).
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setSubmitError(null);
    }
  }, [isOpen, line?.payment_batch_item_id]);

  useEffect(() => {
    setSubmitError(errorMessage);
  }, [errorMessage]);

  const handleReasonChange = (value: string) => {
    setReason(value);
    // Editing the form is a deliberate retry intent — clear the error and
    // re-enable Confirm.
    if (submitError) setSubmitError(null);
  };

  const skip = !isOpen || batchId === null || line === null;
  const { data, isFetching, isError, error } = useGetPaymentBatchClientInvoicesQuery(
    { batch_id: batchId ?? '', client_id: line?.client_id ?? 0 },
    { skip },
  );

  const rows = useMemo(() => (data?.rows ?? []).map((invoice) => ({ ...invoice })), [data?.rows]);

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'reference',
        header: 'Invoice',
        type: 'text',
        deriveValue: (row) => (row.reference as string) || '-',
      },
      {
        id: 'invoice_date',
        header: 'Date',
        type: 'text',
        deriveValue: (row) => formatBatchInvoiceDate(row.invoice_date as string),
        classNames: { header: 'cell-align-center', column: 'cell-align-center' },
      },
      {
        id: 'amount',
        header: 'Amount',
        type: 'text',
        deriveValue: (row) => formatCurrency(row.amount as string),
        classNames: { header: 'cell-align-right', column: 'cell-align-right' },
      },
    ],
    [],
  );

  const setIsOpen = (open: boolean) => {
    if (!open) onCancel();
  };

  const invoiceCount = line?.invoice_count ?? rows.length;

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header={<span id={HEADER_ID}>{line ? `Reverse ${line.client_name}'s payment` : 'Reverse payment'}</span>}
      headerClassName="header"
      size="lg"
      includeFooter={false}
      backdrop="static"
      labelledBy={HEADER_ID}
      data-cy="dd-reverse-modal"
    >
      <div className="d-flex flex-column gap-3" data-cy="dd-reverse-modal-body">
        <div className="d-flex flex-wrap gap-2 column-gap-4 small">
          <span>
            <span className="text-muted">Payment</span> <strong>{line?.reference ?? '-'}</strong>
          </span>
          <span>
            <span className="text-muted">Amount</span> <strong>{formatCurrency(line?.amount ?? '0')}</strong>
          </span>
          {line?.gocardless_payment_id && (
            <span className="w-100 text-muted">
              GoCardless <span className="font-monospace text-break">{line.gocardless_payment_id}</span>
            </span>
          )}
        </div>

        <div className="alert alert-info mb-0" role="note">
          Cancels this payment at GoCardless <strong>before it&apos;s collected</strong>. Its{' '}
          <strong>
            {invoiceCount} invoice{invoiceCount === 1 ? '' : 's'}
          </strong>{' '}
          become collectable again and can go into a future batch. <strong>No money changes hands.</strong>
        </div>

        {isError ? (
          <ErrorResponse message={getErrorMessage(error)} />
        ) : (
          <div>
            {isFetching && !data && (
              <div className="my-2">
                <Spinner loading />
              </div>
            )}
            <TableView
              id="dd-reverse-affected-invoices-table"
              columns={columns}
              data={rows}
              isLoading={isFetching}
              emptyMessage="No invoices found for this payment"
              readonly
              striped
            />
          </div>
        )}

        <div>
          <label htmlFor="dd-reverse-reason" className="fw-semibold">
            Reason <span className="text-muted fw-normal">(optional)</span>
          </label>
          <textarea
            id="dd-reverse-reason"
            data-cy="dd-reverse-reason"
            className="form-control"
            rows={2}
            value={reason}
            placeholder="e.g. Wrong amount - rebatching"
            onChange={(event) => handleReasonChange(event.target.value)}
          />
        </div>

        {submitError && (
          <div className="alert alert-danger mb-0" role="alert" data-cy="dd-reverse-error">
            {submitError}
          </div>
        )}

        <div className="d-flex justify-content-end gap-2">
          <Button
            id="dd-reverse-cancel"
            data-cy="dd-reverse-cancel"
            color="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            id="dd-reverse-confirm"
            data-cy="dd-reverse-confirm"
            color="primary"
            onClick={() => onConfirm(reason.trim())}
            disabled={isSubmitting || Boolean(submitError)}
          >
            Confirm reversal
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReverseModal;
